import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Text,
  useToast,
  Heading,
  Container,
  Center,
  Icon,
  InputGroup,
  InputLeftElement,
  Image,
  Spinner,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { FaFlask, FaKey } from 'react-icons/fa';
import { checkLockout, recordFailedAttempt, clearAttempts } from '../../utils/loginRateLimiter';

const BrandedLabPortal = () => {
  const { slug } = useParams();
  const [secretKey, setSecretKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [labData, setLabData] = useState(null);
  const [labLoading, setLabLoading] = useState(true);
  const [error, setError] = useState(null);
  const toast = useToast();
  const navigate = useNavigate();

  // Fetch lab data based on slug
  useEffect(() => {
    const fetchLabBySlug = async () => {
      try {
        setLabLoading(true);
        const { data, error } = await supabase
          .from('labs')
          .select('*')
          .eq('portal_slug', slug)
          .eq('is_active', true)
          .single();

        if (error || !data) {
          setError('Lab portal not found or inactive');
          return;
        }

        setLabData(data);
      } catch (err) {
        setError('Failed to load lab portal');
      } finally {
        setLabLoading(false);
      }
    };

    if (slug) {
      fetchLabBySlug();
    }
  }, [slug]);

  // Check for existing session
  useEffect(() => {
    const checkSession = () => {
      try {
        const session = localStorage.getItem('labSession');
        if (session) {
          const labSession = JSON.parse(session);
          const loginTime = new Date(labSession.loginTime);
          const now = new Date();
          const hoursDiff = (now - loginTime) / (1000 * 60 * 60);
          
          if (hoursDiff < 24 && labSession.id === labData?.id) {
            // Valid session for this lab, redirect to dashboard
            if (labSession.isNewSignup) {
              // Special handling for new signups - show welcome message
              toast({
                title: 'Welcome to your Lab Portal!',
                description: `Successfully logged in as ${labData.name}`,
                status: 'success',
                duration: 5000,
                isClosable: true,
              });
              // Remove the new signup flag after showing welcome
              labSession.isNewSignup = false;
              localStorage.setItem('labSession', JSON.stringify(labSession));
            }
            navigate('/lab-portal/dashboard');
            return;
          } else {
            // Expired or different lab session, clear it
            localStorage.removeItem('labSession');
          }
        }
      } catch (error) {
        localStorage.removeItem('labSession');
      }
    };
    
    if (labData) {
      checkSession();
    }
  }, [navigate, labData]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Check lockout before attempting login
    const lockout = checkLockout('branded_lab');
    if (lockout.locked) {
      toast({
        title: 'Account Locked',
        description: `Too many failed attempts. Try again in ${lockout.remainingMinutes} minutes.`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setLoading(false);
      return;
    }

    try {
      // Validate secret key for this specific lab (trim to avoid whitespace mismatches)
      const enteredKey = (secretKey || '').trim();
      const storedKey = (labData?.secret_key || '').trim();
      if (!labData || !storedKey || storedKey !== enteredKey) {
        const result = recordFailedAttempt('branded_lab');
        toast({
          title: 'Invalid Secret Key',
          description: result.locked
            ? `Too many failed attempts. Try again in ${result.remainingMinutes} minutes.`
            : `Incorrect secret key. ${result.attemptsRemaining} attempts remaining.`,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      clearAttempts('branded_lab');

      // Record successful login
      const now = new Date().toISOString();
      try {
        // Log the login activity
        await supabase.from('lab_login_log').insert([{
          lab_id: labData.id,
          login_timestamp: now,
          ip_address: null,
          user_agent: navigator.userAgent,
          success: true
        }]);

        // Update last_login_at in labs table
        await supabase
          .from('labs')
          .update({ last_login_at: now })
          .eq('id', labData.id);
      } catch (logError) {
        console.warn('Failed to log login activity:', logError);
      }

      // Store lab session data — never persist secret_key
      const labSession = {
        id: labData.id,
        name: labData.name,
        logo_url: labData.logo_url,
        is_main_key: true,
        loginTime: now,
      };

      localStorage.setItem('labSession', JSON.stringify(labSession));

      // Check if lab should see onboarding guide
      const neverShowOnboarding = localStorage.getItem(`onboarding-never-show-${labData.name}`);
      const hasCompletedOnboarding = localStorage.getItem(`onboarding-completed-${labData.name}`);
      
      // Show onboarding if user hasn't opted out AND lab has never completed onboarding
      const shouldShowOnboarding = (
        neverShowOnboarding !== 'true' && 
        hasCompletedOnboarding !== 'true'
      );

      if (shouldShowOnboarding) {
        // Redirect to login page to trigger onboarding
        navigate('/lab-portal/login', { 
          state: { 
            isNewSignup: false,
            showOnboarding: true,
            labName: labData.name,
            labId: labData.id
          } 
        });
      } else {
        toast({
          title: 'Login Successful',
          description: `Welcome to ${labData.name} portal`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });

        // Redirect to lab portal dashboard
        navigate('/lab-portal/dashboard');
      }
    } catch (error) {
      toast({
        title: 'Login Error',
        description: 'An unexpected error occurred during login.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  if (labLoading) {
    return (
      <Box minH="100vh" bg="gray.50" display="flex" alignItems="center" justifyContent="center">
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.500" />
          <Text>Loading lab portal...</Text>
        </VStack>
      </Box>
    );
  }

  if (error || !labData) {
    return (
      <Box minH="100vh" bg="gray.50" py={8}>
        <Container maxW="md">
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <VStack align="start" spacing={2}>
              <Text fontWeight="bold">Portal Not Found</Text>
              <Text>{error || 'This lab portal does not exist or is inactive.'}</Text>
              <Button size="sm" onClick={() => navigate('/')}>
                Return to Home
              </Button>
            </VStack>
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg="gray.50" py={8}>
      <Container maxW="md">
        <Center mb={8}>
          <VStack spacing={4}>
            <Box p={4} bg="white" borderRadius="full" boxShadow="md">
              {labData.logo_url ? (
                <Image 
                  src={labData.logo_url} 
                  alt={`${labData.name} logo`}
                  boxSize="80px"
                  objectFit="contain"
                />
              ) : (
                <Box p={4} bg="blue.500" borderRadius="full">
                  <Icon as={FaFlask} boxSize={8} color="white" />
                </Box>
              )}
            </Box>
            <VStack spacing={2} textAlign="center">
              <Heading size="lg" color="gray.700">
                {labData.name} Portal
              </Heading>
              <Text color="gray.600" fontSize="md">
                Welcome to your dedicated lab portal
              </Text>
            </VStack>
          </VStack>
        </Center>

        <Box bg="white" p={8} borderRadius="lg" boxShadow="lg">
          <form onSubmit={handleLogin}>
            <VStack spacing={6}>
              <FormControl isRequired>
                <FormLabel>Secret Key</FormLabel>
                <InputGroup>
                  <InputLeftElement>
                    <Icon as={FaKey} color="gray.400" />
                  </InputLeftElement>
                  <Input
                    type="password"
                    value={secretKey}
                    onChange={(e) => setSecretKey(e.target.value)}
                    placeholder="Enter your lab's secret key"
                    size="lg"
                  />
                </InputGroup>
                <Text fontSize="sm" color="gray.500" mt={2}>
                  Contact your system administrator if you don't have your secret key.
                </Text>
              </FormControl>

              <Button
                type="submit"
                colorScheme="blue"
                size="lg"
                width="100%"
                isLoading={loading}
                loadingText="Authenticating..."
              >
                Access {labData.name} Portal
              </Button>
            </VStack>
          </form>
        </Box>

        <Center mt={6}>
          <Text fontSize="sm" color="gray.500">
            This is a secure portal for {labData.name} only.
          </Text>
        </Center>
      </Container>
    </Box>
  );
};

export default BrandedLabPortal; 